/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Directive,
  ElementRef,
  NgModule,
  Output,
  Input,
  EventEmitter,
  OnDestroy,
  AfterContentInit,
  Injectable,
} from '@angular/core';
import {Subject} from 'rxjs/Subject';
import'rxjs/add/operator/debounceTime';
/**
 * Factory that creates a new MutationObserver and allows us to stub it out in unit tests.
 * \@docs-private
 */
export class MdMutationObserverFactory {
/**
 * @param {?} callback
 * @return {?}
 */
create(callback): MutationObserver {
    return typeof MutationObserver === 'undefined' ? null : new MutationObserver(callback);
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdMutationObserverFactory_tsickle_Closure_declarations() {
/** @type {?} */
MdMutationObserverFactory.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdMutationObserverFactory.ctorParameters;
}

/**
 * Directive that triggers a callback whenever the content of
 * its associated element has changed.
 */
export class ObserveContent implements AfterContentInit, OnDestroy {
private _observer: MutationObserver;
/**
 * Event emitted for each change in the element's content.
 */
event = new EventEmitter<MutationRecord[]>();
/**
 * Used for debouncing the emitted values to the observeContent event.
 */
private _debouncer = new Subject<MutationRecord[]>();
/**
 * Debounce interval for emitting the changes.
 */
debounce: number;
/**
 * @param {?} _mutationObserverFactory
 * @param {?} _elementRef
 */
constructor(
private _mutationObserverFactory: MdMutationObserverFactory,
private _elementRef: ElementRef) { }
/**
 * @return {?}
 */
ngAfterContentInit() {
    if (this.debounce > 0) {
      this._debouncer
        .debounceTime(this.debounce)
        .subscribe(mutations => this.event.emit(mutations));
    } else {
      this._debouncer.subscribe(mutations => this.event.emit(mutations));
    }

    this._observer = this._mutationObserverFactory.create((mutations: MutationRecord[]) => {
      this._debouncer.next(mutations);
    });

    if (this._observer) {
      this._observer.observe(this._elementRef.nativeElement, {
        characterData: true,
        childList: true,
        subtree: true
      });
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    if (this._observer) {
      this._observer.disconnect();
      this._debouncer.complete();
      this._debouncer = this._observer = null;
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[cdkObserveContent]'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdMutationObserverFactory, },
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'event': [{ type: Output, args: ['cdkObserveContent', ] },],
'debounce': [{ type: Input },],
};
}

function ObserveContent_tsickle_Closure_declarations() {
/** @type {?} */
ObserveContent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ObserveContent.ctorParameters;
/** @type {?} */
ObserveContent.propDecorators;
/** @type {?} */
ObserveContent.prototype._observer;
/**
 * Event emitted for each change in the element's content.
 * @type {?}
 */
ObserveContent.prototype.event;
/**
 * Used for debouncing the emitted values to the observeContent event.
 * @type {?}
 */
ObserveContent.prototype._debouncer;
/**
 * Debounce interval for emitting the changes.
 * @type {?}
 */
ObserveContent.prototype.debounce;
/** @type {?} */
ObserveContent.prototype._mutationObserverFactory;
/** @type {?} */
ObserveContent.prototype._elementRef;
}

export class ObserveContentModule {static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  exports: [ObserveContent],
  declarations: [ObserveContent],
  providers: [MdMutationObserverFactory]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function ObserveContentModule_tsickle_Closure_declarations() {
/** @type {?} */
ObserveContentModule.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ObserveContentModule.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
